import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Layout from '../../components/Layout';
import { GitHubConnectBanner } from '../../components/GitHubConnectBanner';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd } from '../../components/StructuredData';
import SEO from '../../components/SEO';
import { useAuthStore } from '../../stores/authStore';
import { useToolReadiness } from '../../hooks/useToolReadiness';

/**
 * Tools Index Page
 * Landing page for satellite tools - allows users to select which tool to use
 */
export const ToolsIndexPage: React.FC = () => {
  const authHydrated = useAuthStore((state) => state._hasHydrated);
  const storeAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const readiness = useToolReadiness('tools');
  const readinessAuth = readiness.data?.isAuthenticated;
  const isAuthenticated = readinessAuth ?? (authHydrated ? storeAuthenticated : undefined);
  const tools = [
    {
      id: 'ai-risk-classifier',
      title: 'AI Risk Classifier',
      description: 'Classify your AI system under the EU AI Act in 2 minutes. Get instant risk level and compliance checklist.',
      timing: 'Free • No signup required',
      icon: '🎯',
      href: '/tools/ai-risk-classifier',
      features: [
        '2-minute AI system assessment',
        'EU AI Act risk classification (Unacceptable/High/Limited/Minimal)',
        'Compliance checklist and required documentation',
        'Deadline tracking (Aug 2, 2026)',
      ],
      badge: 'NEW',
      noAuthRequired: true,
    },
    {
      id: 'pr-approvals',
      title: 'PR Approvals Report',
      description: 'Generate evidence packs showing pull request approvals, reviewers, timestamps, and approval thresholds.',
      timing: 'Generate in ~60 seconds',
      icon: '✅',
      href: '/tools/pr-approvals-report',
      features: [
        'Pull request approval history',
        'Reviewer details and timestamps',
        'Approval threshold evaluation',
        'Multi-format export (PDF, CSV, JSON, manifest)',
      ],
    },
    {
      id: 'branch-protection',
      title: 'Branch Protection Snapshot',
      description: 'Capture point-in-time snapshots of branch protection settings and generate drift diffs.',
      timing: 'Generate in ~60 seconds',
      icon: '🛡️',
      href: '/tools/branch-protection-snapshot',
      features: [
        'Protection settings snapshot',
        'Drift diff & severity levels',
        'Multi-branch support',
        'Tamper-evident checksums',
      ],
    },
  ];

  const layoutProps = {
    title: 'GitHub Evidence Tools | EvidentTrail',
    description: 'Generate PR approval and branch protection evidence packs with PDF, CSV, JSON, and manifest exports. Guest mode supports anonymous exports.',
    h1: 'GitHub Evidence Tools',
    heroText: 'Generate evidence packs for PR approvals and branch protection snapshots with tamper-evident checksums to support SOC 2 and ISO 27001 readiness.'
  };

  const toolsGrid = (
    <div className={tokens.container}>
      <div className="grid md:grid-cols-2 gap-8 mb-16">
        {tools.map((tool: any) => (
          <Link
            key={tool.id}
            to={tool.href}
            className={`group rounded-2xl p-8 transition-all duration-300 flex flex-col h-full relative ${
              isAuthenticated
                ? 'bg-slate-800/60 border border-slate-700/70 hover:border-blue-500/60 hover:bg-slate-800/80'
                : 'bg-slate-800/50 border border-slate-700/50 hover:border-blue-500/50 hover:bg-slate-800/70'
            }`}
          >
            {tool.badge && (
              <div className="absolute top-4 right-4 px-3 py-1 bg-primary-500 text-white text-xs font-bold rounded-full">
                {tool.badge}
              </div>
            )}
            <div className="text-5xl mb-4 transform group-hover:scale-110 transition-transform duration-300">{tool.icon}</div>
            <h2 className="text-2xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">
              {tool.title}
            </h2>
            <p className="text-slate-400 mb-3 flex-grow">
              {tool.description}
            </p>
            <p className="text-sm text-blue-300 mb-6">{tool.timing}</p>
            <ul className="space-y-2 mb-8">
              {tool.features.map((feature: string, idx: number) => (
                <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                  <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
            <div className="mt-auto flex items-center gap-2 text-blue-400 font-medium group-hover:gap-3 transition-all pt-4 border-t border-slate-700/50">
              {tool.noAuthRequired ? 'Try for free →' : (isAuthenticated ? 'Open tool' : 'Connect GitHub to generate')}
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );

  const guestContent = (
    <>
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'Tools', url: 'https://evidenttrail.com/tools' }
        ]}
      />
      <div className={tokens.container}>
        <div className="max-w-3xl mx-auto text-center mb-10 text-slate-300">
          <p>
            Start in guest mode to export a single evidence pack without creating an account. Connect GitHub to
            unlock multi-repo exports, retention, and scheduled evidence packs in the core platform.
          </p>
        </div>
        <GitHubConnectBanner className="mb-16" />
      </div>

      {toolsGrid}

      <div className={tokens.container}>
        <div className="rounded-2xl p-8 mb-16 border bg-slate-900/50 border-slate-700/60">
          <h2 className="text-2xl font-semibold text-white mb-4">Which tool should I use?</h2>
          <div className="grid md:grid-cols-3 gap-6 text-slate-300 text-sm">
            <div>
              <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                AI Risk Classifier 
                <span className="text-xs px-2 py-0.5 bg-primary-500 text-white rounded-full">NEW</span>
              </h3>
              <ul className="space-y-2">
                <li>• Need to classify AI systems under EU AI Act</li>
                <li>• Understand compliance requirements for AI projects</li>
                <li>• Prepare for August 2, 2026 deadline</li>
                <li>• Free tool, no GitHub connection required</li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">PR Approvals Report</h3>
              <ul className="space-y-2">
                <li>• Audit samples that need proof of approvals and review timing</li>
                <li>• Evidence aligned to SOC 2 CC8.1 and ISO change control reviews</li>
                <li>• Quick exports for security questionnaires</li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">Branch Protection Snapshot</h3>
              <ul className="space-y-2">
                <li>• Proof of branch protection settings at a point in time</li>
                <li>• Drift comparisons between snapshots</li>
                <li>• Evidence of required reviews and required checks</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
          <Link to="/read-only-github-audit" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
            <h3 className="text-white font-semibold mb-2">Read-Only GitHub Audit</h3>
            <p className="text-sm text-slate-300">Secure evidence collection without write access.</p>
          </Link>
          <Link to="/audit-readiness" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
            <h3 className="text-white font-semibold mb-2">Audit Readiness</h3>
            <p className="text-sm text-slate-300">Prepare evidence ahead of audit sampling windows.</p>
          </Link>
          <Link to="/github-change-control" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
            <h3 className="text-white font-semibold mb-2">GitHub Change Control</h3>
            <p className="text-sm text-slate-300">Map PR evidence to change control expectations.</p>
          </Link>
          <Link to="/secure-evidence-sharing" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
            <h3 className="text-white font-semibold mb-2">Secure Evidence Sharing</h3>
            <p className="text-sm text-slate-300">Provide time-limited auditor access to evidence packs.</p>
          </Link>
          <Link to="/security" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
            <h3 className="text-white font-semibold mb-2">Security</h3>
            <p className="text-sm text-slate-300">Read-only access and data minimization boundaries.</p>
          </Link>
        </div>

        <div className="text-center py-12 border-t border-slate-800/50">
          <h3 className="text-xl font-semibold text-white mb-4">More Tools Coming Soon</h3>
          <p className="text-slate-400 max-w-xl mx-auto">
            Jira and Azure DevOps integrations are on the roadmap for future evidence capture.
            Have a specific requirement?
            <a href="mailto:support@evidenttrail.com" className="text-blue-400 hover:text-blue-300 ml-1 hover:underline">
              Let us know
            </a>.
          </p>
        </div>
      </div>
    </>
  );

  if (isAuthenticated === undefined) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-slate-400">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <Layout skipTenantRedirect>
        <SEO title={layoutProps.title} description={layoutProps.description} />
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-white mb-2">GitHub Evidence Tools</h1>
          <p className="text-slate-300">
            Generate PR approval and branch protection evidence packs with tamper-evident exports.
          </p>
        </div>
        {toolsGrid}
      </Layout>
    );
  }

  return (
    <MarketingLayout {...layoutProps}>
      {guestContent}
    </MarketingLayout>
  );
};

export default ToolsIndexPage;
